import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePageLoading } from '../context/PageLoadingContext';
import { FaBox, FaShoppingCart, FaTruck, FaChartBar, FaSignOutAlt, FaBars, FaUserCircle, FaTags, FaUsers, FaTimes, FaChevronRight, FaClipboardList, FaCashRegister, FaStore, FaFileInvoiceDollar, FaUserShield, FaAddressBook, FaWarehouse, FaHome, FaWallet, FaMobileAlt } from 'react-icons/fa';
import './Layout.css';

const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarPinned, setSidebarPinned] = useState(false);
  const location = useLocation();
  const { isPageLoading } = usePageLoading();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // Escala de interfaz por dispositivo (para tablets pequeñas). Se guarda en localStorage.
  const [uiScale, setUiScale] = useState(() => parseFloat(localStorage.getItem('uiScale') || '1') || 1);
  const [scaleVisible, setScaleVisible] = useState(false);
  const scaleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    (document.documentElement.style as any).zoom = String(uiScale);
    // Exponer la escala para que los modales compensen las alturas en vh (el zoom las multiplica)
    document.documentElement.style.setProperty('--ui-scale', String(uiScale));
    localStorage.setItem('uiScale', String(uiScale));
  }, [uiScale]);

  const armScaleHide = (ms = 5000) => {
    if (scaleTimer.current) clearTimeout(scaleTimer.current);
    scaleTimer.current = setTimeout(() => setScaleVisible(false), ms);
  };
  const flashScale = () => { setScaleVisible(true); armScaleHide(); };
  const changeScale = (delta: number) => {
    setUiScale(s => Math.min(1.6, Math.max(0.9, Math.round((s + delta) * 100) / 100)));
    flashScale();  // reinicia los 5s mientras se ajusta
  };

  // Mostrar el control (y auto-ocultar a los 5s) al abrir/expandir el menú lateral
  useEffect(() => {
    if (isMobile ? sidebarOpen : sidebarPinned) flashScale();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebarOpen, sidebarPinned, isMobile]);

  // Color de marca del tenant -> botones y sidebar
  useEffect(() => {
    const color = (user?.tenantSettings as any)?.tienda?.primaryColor || '#2563eb';
    document.documentElement.style.setProperty('--brand', color);
    const m = color.replace('#', '').match(/.{2}/g);
    if (m) document.documentElement.style.setProperty('--brand-rgb', m.map((x: string) => parseInt(x, 16)).join(','));
  }, [user?.tenantSettings]);

  useEffect(() => {
    if (sidebarOpen && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [sidebarOpen, isMobile]);

  const handleLogout = () => { logout(); navigate('/login'); };
  const closeSidebar = () => setSidebarOpen(false);

  const isActive = (path: string) => location.pathname === path;

  const navItems = [
    ...(user?.role === 'administrador' ? [{ path: '/inicio', icon: <FaHome />, label: 'Inicio' }] : []),
    ...(user?.role === 'administrador' ? [{ path: '/categorias', icon: <FaTags />, label: 'Categorias' }] : []),
    { path: '/productos', icon: <FaBox />, label: 'Productos' },
    ...(user?.role === 'administrador' ? [{ path: '/usuarios', icon: <FaUsers />, label: 'Usuarios' }] : []),
    ...((user?.role === 'administrador' || user?.role === 'supervisor') ? [{ path: '/compras', icon: <FaTruck />, label: 'Compras' }] : []),
    ...(user?.role === 'administrador' ? [{ path: '/inventario', icon: <FaClipboardList />, label: 'Inventario' }] : []),
    ...((user?.role === 'administrador' || user?.role === 'supervisor') ? [{ path: '/movimientos', icon: <FaWarehouse />, label: 'Movimientos' }] : []),
    ...(user?.role === 'administrador' && user?.tenantSettings?.cash_management_enabled ? [{ path: '/caja', icon: <FaCashRegister />, label: 'Caja' }] : []),
    ...(user?.isSuperAdmin ? [{ path: '/super-admin', icon: <FaUserShield />, label: 'Super-Admin' }] : []),
    { path: '/ventas', icon: <FaShoppingCart />, label: 'POS' },
    ...(user?.tenantSettings?.tienda?.habilitada ? [{ path: '/pedidos', icon: <FaStore />, label: 'Pedidos' }] : []),
    ...(user?.role === 'administrador' && user?.tenantSettings?.tienda?.habilitada ? [{ path: '/clientes', icon: <FaAddressBook />, label: 'Clientes' }] : []),
    ...(user?.role === 'administrador' ? [{ path: '/mi-tienda', icon: <FaStore />, label: 'Mi tienda' }] : []),
    ...(user?.role === 'administrador' ? [{ path: '/facturacion', icon: <FaFileInvoiceDollar />, label: 'Comprobantes' }] : []),
    { path: '/reportes', icon: <FaChartBar />, label: 'Reportes' },
    ...(user?.role === 'administrador' && (user?.tenantSettings as any)?.capital_enabled ? [{ path: '/capital', icon: <FaWallet />, label: 'Capital' }] : []),
    ...((user?.role === 'administrador' || user?.role === 'supervisor') && (user?.tenantSettings as any)?.yape_listener_enabled ? [{ path: '/yapes', icon: <FaMobileAlt />, label: 'Yapes' }] : []),
  ];

  const roleBadge = user?.role === 'administrador' ? { bg: '#3b82f6', label: 'Admin' }
    : user?.role === 'supervisor' ? { bg: '#f59e0b', label: 'Supervisor' }
    : { bg: '#06b6d4', label: 'Vendedor' };

  return (
    <div className="layout-container">
      {/* Mobile toggle */}
      {isMobile && (
        <button className="mobile-menu-btn" onClick={() => setSidebarOpen(true)} aria-label="Menu">
          <FaBars />
        </button>
      )}

      {/* Overlay mobile */}
      {isMobile && sidebarOpen && (
        <div className="sidebar-overlay open" onClick={closeSidebar} />
      )}

      {/* Sidebar */}
      <aside
        className={`sidebar ${isMobile ? (sidebarOpen ? 'mobile-open' : 'mobile-closed') : (sidebarPinned ? 'expanded' : '')}`}
        onMouseEnter={() => !isMobile && setSidebarPinned(true)}
        onMouseLeave={() => !isMobile && setSidebarPinned(false)}
      >
        {/* Header */}
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <img src="/logo-tuplazita.svg" alt="TuPlazita" className="sidebar-logo-img" />
            <span className="sidebar-brand">{user?.tenantName || 'Quiosco'}</span>
          </div>
          {isMobile && (
            <button className="sidebar-close" onClick={closeSidebar}><FaTimes /></button>
          )}
        </div>

        {/* User info */}
        <div className="sidebar-user">
          <div className="sidebar-avatar">
            <FaUserCircle />
          </div>
          <div className="sidebar-user-info">
            <div className="sidebar-user-name">{user?.name || 'Usuario'}</div>
            <span className="sidebar-role-badge" style={{ background: roleBadge.bg }}>{roleBadge.label}</span>
          </div>
        </div>

        <div className="sidebar-divider" />

        {/* Navigation */}
        <nav className="sidebar-nav">
          {navItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`sidebar-nav-item ${isActive(item.path) ? 'active' : ''}`}
              onClick={closeSidebar}
              title={item.label}
            >
              <span className="sidebar-nav-icon">{item.icon}</span>
              <span className="sidebar-nav-label">{item.label}</span>
              {isActive(item.path) && <FaChevronRight className="sidebar-nav-indicator" />}
            </Link>
          ))}
        </nav>

        {/* Footer */}
        <div className="sidebar-footer">
          <button className="sidebar-nav-item logout" onClick={handleLogout} title="Cerrar Sesion">
            <span className="sidebar-nav-icon"><FaSignOutAlt /></span>
            <span className="sidebar-nav-label">Salir</span>
          </button>
          <div className="sidebar-version">v{process.env.REACT_APP_VERSION || '1.0.0'}</div>
        </div>
      </aside>

      {/* Main content */}
      <main className={`main-content ${!isMobile && sidebarPinned ? 'sidebar-expanded' : ''}`}>
        <Outlet />
      </main>

      {/* Overlay global de carga */}
      {isPageLoading && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',zIndex:9999,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:'1.5rem',userSelect:'none',pointerEvents:'all'}}>
          <img src="/logo-loading.svg" alt="Cargando..." style={{width:88,height:88,animation:'spin 1.2s linear infinite'}} />
          <div style={{color:'#fff',fontSize:'1.25rem',fontWeight:600,letterSpacing:'0.02em'}}>Cargando...</div>
        </div>
      )}

      {/* Control de tamaño — aparece al abrir el menú lateral y se oculta a los 5s */}
      {scaleVisible && (
        <div
          onMouseEnter={() => { if (scaleTimer.current) clearTimeout(scaleTimer.current); if (!isMobile) setSidebarPinned(true); }}
          onMouseLeave={() => { armScaleHide(); if (!isMobile) setSidebarPinned(false); }}
          style={{ position: 'fixed', right: 12, bottom: 12, zIndex: 15000, display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(15,23,42,0.92)', backdropFilter: 'blur(4px)', borderRadius: 24, padding: '6px 10px', boxShadow: '0 4px 16px rgba(0,0,0,0.3)', animation: 'fadeIn .2s ease' }}
          title="Tamaño de interfaz"
        >
          <span style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, marginRight: 2 }}>Tamaño</span>
          <button onClick={() => changeScale(-0.1)} disabled={uiScale <= 0.9} aria-label="Interfaz más pequeña" style={{ background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', width: 30, height: 28, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: uiScale <= 0.9 ? 0.4 : 1 }}>A−</button>
          <span style={{ color: '#e2e8f0', fontSize: 12, fontWeight: 700, minWidth: 36, textAlign: 'center' }}>{Math.round(uiScale * 100)}%</span>
          <button onClick={() => changeScale(0.1)} disabled={uiScale >= 1.6} aria-label="Interfaz más grande" style={{ background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', width: 30, height: 28, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: uiScale >= 1.6 ? 0.4 : 1 }}>A+</button>
        </div>
      )}
    </div>
  );
};

export default Layout;
